import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  Download,
  Eye,
  Shield,
  User,
  Clock,
  Loader2,
  X,
} from 'lucide-react';
import { getAuditLogs, AuditLogEntry, AuditLogStats } from '../../services/MasterAdmin/accountantService';

const AuditLogsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>('ALL');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('ALL');
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAuditLogs({
        entityType: entityTypeFilter !== 'ALL' ? (entityTypeFilter as 'SETTLEMENT' | 'PAYOUT' | 'LEDGER') : undefined,
        actionType: actionTypeFilter !== 'ALL' ? actionTypeFilter : undefined,
        search: searchTerm || undefined,
        page,
        limit: 50,
      });
      setAuditLogs(response.data.auditLogs);
      setStats(response.data.stats);
      setTotalPages(response.pagination.totalPages);
    } catch (err: any) {
      console.error('Error fetching audit logs:', err);
      setError(err.response?.data?.message || 'Failed to fetch audit logs');
      setAuditLogs([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, actionTypeFilter, searchTerm, page]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getEntityTypeBadge = (type: string) => {
    const typeConfig: Record<string, string> = {
      SETTLEMENT: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      PAYOUT: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      LEDGER: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeConfig[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  const getActionBadge = (action: string) => {
    const actionConfig: Record<string, string> = {
      SETTLEMENT_INITIATED: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      SETTLEMENT_COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      PAYOUT_RETRY: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      MANUAL_ADJUSTMENT: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      SETTLEMENT_FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      SETTLEMENT_CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      LEDGER_ENTRY_CREATED: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
      LEDGER_ENTRY_UPDATED: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      LEDGER_ENTRY_ADJUSTED: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionConfig[action] || 'bg-gray-100 text-gray-800'}`}>
        {action.replace(/_/g, ' ')}
      </span>
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleEntityTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEntityTypeFilter(e.target.value);
    setPage(1);
  };

  const handleActionTypeFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActionTypeFilter(e.target.value);
    setPage(1);
  };

  const handleViewDetails = (log: AuditLogEntry) => {
    setSelectedLog(log);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">Audit & Compliance</h2>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Immutable audit logs for all financial transactions and changes
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <button className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
            <Download className="w-4 h-4" />
            <span>Export Logs</span>
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-300">
              Audit Trail Protection
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
              All financial actions are logged with timestamps and user information. These logs are
              immutable and cannot be deleted or modified.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search audit logs..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={entityTypeFilter}
            onChange={handleEntityTypeFilterChange}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Entity Types</option>
            <option value="SETTLEMENT">Settlement</option>
            <option value="PAYOUT">Payout</option>
            <option value="LEDGER">Ledger</option>
          </select>
          <select
            value={actionTypeFilter}
            onChange={handleActionTypeFilterChange}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Actions</option>
            <option value="SETTLEMENT_INITIATED">Settlement Initiated</option>
            <option value="SETTLEMENT_COMPLETED">Settlement Completed</option>
            <option value="PAYOUT_RETRY">Payout Retry</option>
            <option value="MANUAL_ADJUSTMENT">Manual Adjustment</option>
            <option value="LEDGER_ENTRY_UPDATED">Ledger Entry Updated</option>
          </select>
          <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2">
            <Filter className="w-4 h-4" />
            <span>More Filters</span>
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Logs</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {loading ? '...' : stats?.totalLogs || 0}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Initiated</p>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400 mt-1">
            {loading ? '...' : stats?.initiatedCount || 0}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {loading ? '...' : stats?.completedCount || 0}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Retries</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
            {loading ? '...' : stats?.retryCount || 0}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Adjustments</p>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-1">
            {loading ? '...' : stats?.adjustmentCount || 0}
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entity Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entity ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Remarks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {auditLogs.map((log) => (
                  <tr key={log.auditLogId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(log.performedAt)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getActionBadge(log.actionType)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{getEntityTypeBadge(log.entityType)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                        {log.entityId.length > 20 ? `${log.entityId.substring(0, 20)}...` : log.entityId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm text-gray-900 dark:text-white">
                            {log.performedByName || log.performedBy}
                          </div>
                          {log.performedByRole && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {log.performedByRole}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                        {log.remarks || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(log)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Page {page} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {!loading && auditLogs.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">No audit logs found matching your filters.</p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Audit Log Details
              </h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Action Type</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {getActionBadge(selectedLog.actionType)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Entity Type</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {getEntityTypeBadge(selectedLog.entityType)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Entity ID</p>
                  <p className="text-sm text-gray-900 dark:text-white font-mono mt-1">
                    {selectedLog.entityId}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Performed At</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {formatDate(selectedLog.performedAt)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Performed By</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">
                    {selectedLog.performedByName || selectedLog.performedBy}
                  </p>
                  {selectedLog.performedByRole && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Role: {selectedLog.performedByRole}
                    </p>
                  )}
                </div>
                {selectedLog.vendorId && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Vendor ID</p>
                    <p className="text-sm text-gray-900 dark:text-white font-mono mt-1">
                      {selectedLog.vendorId}
                    </p>
                  </div>
                )}
              </div>
              {(selectedLog.oldStatus || selectedLog.newStatus) && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status Change</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedLog.oldStatus || 'N/A'}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm text-gray-900 dark:text-white font-medium">
                      {selectedLog.newStatus || 'N/A'}
                    </span>
                  </div>
                </div>
              )}
              {(selectedLog.oldAmount !== null || selectedLog.newAmount !== null) && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Amount Change</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedLog.oldAmount !== null ? `₹${selectedLog.oldAmount.toFixed(2)}` : 'N/A'}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm text-gray-900 dark:text-white font-medium">
                      {selectedLog.newAmount !== null ? `₹${selectedLog.newAmount.toFixed(2)}` : 'N/A'}
                    </span>
                  </div>
                </div>
              )}
              {selectedLog.remarks && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Remarks</p>
                  <p className="text-sm text-gray-900 dark:text-white mt-1">{selectedLog.remarks}</p>
                </div>
              )}
              {selectedLog.oldValues && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Old Values</p>
                  <pre className="text-xs text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 p-3 rounded mt-1 overflow-x-auto">
                    {JSON.stringify(selectedLog.oldValues, null, 2)}
                  </pre>
                </div>
              )}
              {selectedLog.newValues && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">New Values</p>
                  <pre className="text-xs text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 p-3 rounded mt-1 overflow-x-auto">
                    {JSON.stringify(selectedLog.newValues, null, 2)}
                  </pre>
                </div>
              )}
              {selectedLog.ipAddress && (
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</p>
                  <p className="text-sm text-gray-900 dark:text-white font-mono mt-1">
                    {selectedLog.ipAddress}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
